import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

/**
 * ProtectedRoute component that checks authentication before allowing access to protected pages
 * If user is not authenticated, stores the current path and redirects to login
 * After login, user will be redirected back to the original page
 *
 * Note: This only checks for token existence locally. Actual token validity is checked
 * by baseapiservice when API calls are made - it will redirect to login on session expiry.
 */
export default function ProtectedRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuthentication = () => {
      // Check for token in localStorage or sessionStorage
      const token =
        localStorage.getItem('x-access-token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('x-access-token') ||
        sessionStorage.getItem('token');

      if (!token) {
        console.log('[ProtectedRoute] No token found, redirecting to login');
        // Store current path for redirect after login
        const currentPath = location.pathname + location.search;
        sessionStorage.setItem('redirectAfterLogin', currentPath);
        navigate('/citizen-login', { replace: true });
        return;
      }

      // Token exists, allow access
      // Note: If the token is expired, baseapiservice will detect it when API calls are made
      // and will automatically redirect to login
      console.log('[ProtectedRoute] Token found, allowing access');
      setIsCheckingAuth(false);
    };

    checkAuthentication();
  }, [navigate, location]);

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // User has token, render the protected route
  return <Outlet />;
}

